import { useEffect, useState } from "react";
import api, { extractErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Languages, Save, Loader2, Youtube } from "lucide-react";
import { SECTION_TITLES } from "@/components/TutorialBanner";

const SECTION_FIELDS = [
  { key: "dashboard", hint: "Merchant Dashboard — popup tutorial (defaults to the intro video if left blank)", testId: "i18n-section-dashboard" },
  { key: "domain", hint: "Settings → Domain", testId: "i18n-section-domain" },
  { key: "email", hint: "Settings → Email (SMTP)", testId: "i18n-section-email" },
  { key: "products", hint: "Products — add a product and variations", testId: "i18n-section-products" },
  { key: "custom_print", hint: "Add/Edit product → Custom Print (shown when the toggle is on)", testId: "i18n-section-custom-print" },
  { key: "car_parts", hint: "Add/Edit product → Car Parts fitment (shown when the addon is on)", testId: "i18n-section-car-parts" },
];

const SECTION_LABELS = {
  custom_print: "Custom Print Youtube Video",
  car_parts: "Car Parts Youtube Video",
};

export default function SuperTranslation() {
  const [langs, setLangs] = useState([]);
  const [tutorials, setTutorials] = useState({});
  const [sections, setSections] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/platform/i18n/tutorials").then(({ data }) => {
      setLangs(data.languages || []);
      setTutorials(data.tutorials || {});
      setSections(data.sections || {});
    }).catch((e) => toast.error(extractErr(e)));
  }, []);

  const setRow = (code, patch) => setTutorials((cur) => ({ ...cur, [code]: { ...(cur[code] || {}), ...patch } }));
  const setSection = (key, youtube_url) => setSections((cur) => ({ ...cur, [key]: { ...(cur[key] || {}), youtube_url } }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/platform/i18n/tutorials", { tutorials, sections });
      setTutorials(data.tutorials || tutorials);
      setSections(data.sections || sections);
      toast.success("Tutorials saved");
    } catch (e) { toast.error(extractErr(e)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-100 flex items-center gap-2"><Languages className="w-7 h-7 text-orange-300" /> Translation</h1>
        <p className="text-slate-400 text-sm mt-1">YouTube tutorials for the merchant dashboard. Section links open in a popup. Per-language links still appear on Add product when the dashboard is not English. UI copy lives in locale JSON files — not Google Translate.</p>
      </div>

      <Card className="bg-slate-900 border-slate-800 p-5 space-y-4 text-slate-100" data-testid="i18n-section-tutorials">
        <div className="flex items-center gap-2 text-red-300">
          <Youtube className="w-4 h-4" />
          <span className="text-sm font-semibold">Merchant dashboard tutorials</span>
        </div>
        <p className="text-xs text-slate-500">Paste a YouTube watch, youtu.be, or embed URL. Leave a field empty to hide that banner (Dashboard falls back to the intro video).</p>
        {SECTION_FIELDS.map((f) => (
          <div key={f.key}>
            <Label>{SECTION_LABELS[f.key] || SECTION_TITLES[f.key]}</Label>
            <p className="text-[11px] text-slate-500 mt-0.5">{f.hint}</p>
            <Input
              className="mt-1 bg-slate-800 border-slate-700 text-white"
              value={(sections[f.key] || {}).youtube_url || ""}
              onChange={(e) => setSection(f.key, e.target.value)}
              placeholder="https://www.youtube.com/watch?v=…"
              data-testid={f.testId}
            />
          </div>
        ))}
      </Card>

      {(langs.filter((l) => l.code !== "en")).map((l) => {
        const row = tutorials[l.code] || {};
        return (
          <Card key={l.code} className="bg-slate-900 border-slate-800 p-5 space-y-3 text-slate-100" data-testid={`i18n-tut-${l.code}`}>
            <div className="font-semibold">{l.native} <span className="text-slate-500 font-normal">({l.name})</span></div>
            <p className="text-xs text-slate-500">Typing tool: <a className="text-indigo-300 underline" href={l.typing_url} target="_blank" rel="noreferrer">{l.typing_url}</a></p>
            <div>
              <Label>YouTube tutorial URL</Label>
              <Input className="mt-1 bg-slate-800 border-slate-700 text-white" value={row.youtube_url || ""} onChange={(e) => setRow(l.code, { youtube_url: e.target.value })} placeholder="https://www.youtube.com/watch?v=…" data-testid={`i18n-yt-${l.code}`} />
            </div>
            <div>
              <Label>Button title (optional)</Label>
              <Input className="mt-1 bg-slate-800 border-slate-700 text-white" value={row.title || ""} onChange={(e) => setRow(l.code, { title: e.target.value })} placeholder={`How to add a product in ${l.name}`} />
            </div>
          </Card>
        );
      })}
      <Button onClick={save} disabled={saving} data-testid="i18n-tut-save" className="bg-indigo-600 hover:bg-indigo-500">
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save tutorials
      </Button>
    </div>
  );
}
